import * as Sentry from '@sentry/react';
import { Auth } from 'aws-amplify';
import postal from 'postal';
import type { RxDatabase } from 'rxdb';
import { RxGraphQLReplicationState } from 'rxdb/dist/types/plugins/replication-graphql';

import { AimsDatabaseCollections } from '../models/models';
import { isJestTest } from '../test-helpers/isJestTest';
import initializeDB from './initializeDB';
import { refreshTokenOnSyncState } from './refreshToken';
import syncReplicationStatesWithGraphQL from './syncReplicationStatesWithGraphQL';

const getIdToken = async (): Promise<string> => {
  const currentSession = await Auth.currentSession();
  const idToken = currentSession.getIdToken().getJwtToken();
  return idToken;
};

export default class RxdbManager {
  private static _instance: RxdbManager;

  db: RxDatabase<AimsDatabaseCollections> | undefined;

  isInitialized: boolean;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  syncStates: RxGraphQLReplicationState<any>[];

  syncActive: boolean;

  DEBUG: boolean;

  syncCancelled: boolean;

  constructor(DEBUG = true, providedDb?: RxDatabase) {
    this.db = undefined;
    this.syncStates = [];
    this.isInitialized = false;
    this.syncActive = false;
    this.syncCancelled = false;
    this.DEBUG = DEBUG;
    RxdbManager._instance = this;
    if (providedDb) this.db = providedDb;
    this.initialize();
  }

  public static get instance(): RxdbManager {
    return RxdbManager._instance;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public static get syncStates(): RxGraphQLReplicationState<any>[] {
    return this.syncStates;
  }

  public initialize = async () => {
    // No need to run during tests which have import trail back to RxdbManager;
    if (isJestTest()) {
      if (!this.db) {
        // Recreate db for tests
        let { db } = await initializeDB();
        await db?.remove();
        ({ db } = await initializeDB());
      }
      this.isInitialized = true;
      return;
    }

    if (this.DEBUG) console.log('initializing db...');
    const rxdbInfo = await initializeDB();
    this.db = rxdbInfo.db;
    this.isInitialized = true;
    if (this.DEBUG) console.log('Publishing db.initialized.');
    postal.publish({
      channel: 'db',
      topic: 'db.initialized',
    });
  };

  startReplication = async (flags?: { [key: string]: boolean }) => {
    if (this.DEBUG) console.log('Start Rxdb replication');
    if (this.syncCancelled) {
      if (this.DEBUG) console.log('Sync cancelled, cannot start Replication');
      return;
    }
    if (isJestTest()) return;

    // if the db is not initialized, we can't start replication
    if (!this.isInitialized) return;

    const allSyncStatesRunning =
      this.syncStates.length > 0 &&
      this.syncStates.every(state => {
        return state.isStopped() === false;
      });

    if (this.DEBUG) console.table({ allSyncStatesRunning });

    await this.stopReplication();

    const idToken = await getIdToken();

    try {
      const { syncStates } = await syncReplicationStatesWithGraphQL(idToken, this.db);

      this.syncStates = syncStates;
      if (this.DEBUG) {
        const allRunning = this.syncStates.every(state => !state.isStopped());
        console.log(
          `Start replication complete... ${this.syncStates.length} sync states. All running?: ${allRunning}`,
        );
      }

      this.syncActive = true;

      postal.publish({
        channel: 'sync',
        topic: 'sync.online',
      });

      if (flags?.isShowSync) {
        postal.publish({
          channel: 'sync',
          topic: 'sync.show',
        });
      }
    } catch (err) {
      console.log('🚀 ~ file: RxdbManager.ts:130 ~ RxdbManager ~ startReplication= ~ err:', err);
      Sentry.captureException(err, {
        tags: {
          rxDBError: true,
          startReplicationFailure: true,
        },
      });
    }
  };

  stopReplication = async (preventRestart = false) => {
    // preventRestart will never allow sync to start again

    if (this.DEBUG) console.log('Stop Rxdb replication');
    if (!this.isInitialized || isJestTest()) return;
    if (this.DEBUG) console.log('stopReplication called...');

    if (this.syncStates) {
      await Promise.all(this.syncStates.map(state => state.cancel()));

      const allStopped = this.syncStates.every(state => state.isStopped());
      console.log('Stop replication completed:', allStopped);
    }
    this.syncActive = false;
    if (preventRestart) this.syncCancelled = true;
  };

  runSingleReplication = async () => {
    if (this.DEBUG) console.log('run single replication called...');
    try {
      if (this.syncStates.length) {
        await Promise.all(
          this.syncStates.map(async state => {
            if (this.DEBUG) console.log(`running on ${state.collection.name}...`);
            return state.run();
          }),
        );
        if (this.DEBUG) console.log('completed single replication...!!!');
      }
    } catch (error) {
      console.error('🚀 ~ file: RxdbManager.ts ~ line 123 ~ runSingleReplication ~ error', error);
      throw error;
    }
  };

  refreshTokensOnAllCollections = async () => {
    if (this.syncStates) {
      console.log('Calling refresh token on all collections...');

      for (const state of this.syncStates) {
        await refreshTokenOnSyncState(state, 6 * 60 * 60);
      }
    }
  };
}
